import { FC, useMemo } from "react";
import TableComponent, {
  TableProps,
} from "../../../../../common/components/table/table.component";
import { useAppContext } from "../../../../../context/app.context";
import orderOptimizerCargoDetailsBeforeOptimizationTableHelper from "./order-optimizer-cargo-details-before-optimization-table.helper";
import OrderOptimizerCargoDetailsBeforeOptimizationTableRow from "../common/types/order-optimizer-cargo-details-before-optimization-table-row";
import orderOptimizerTranslationsHelper from "../../../../../languages/order-optimizer-translations.helper";
import uuidService from "../../../../../common/utils/uuid/uuid.service";
import {
  OrderOptimizerCargoDetailsItemOptimizedStats,
  OrderOptimizerCargoDetailsItemOrders,
  OrderOptimizerCargoDetailsItemUnoptimizedStats,
} from "../common/types/order-optimizer-cargo-details-item";
import orderOptimizerCargoDetailsHelper from "../order-optimizer-cargo-details.helper";

type OrderOptimizerCargoDetailsBeforeOptimizationTableProps = Pick<
  TableProps,
  "isError" | "isLoading"
> & {
  detailsOptimizedStatsItem?: OrderOptimizerCargoDetailsItemOptimizedStats;
  detailsUnoptimizedStatsItem?: OrderOptimizerCargoDetailsItemUnoptimizedStats;
  detailsOrderItems?: OrderOptimizerCargoDetailsItemOrders[];
};

const OrderOptimizerCargoDetailsBeforeOptimizationTableComponent: FC<
  OrderOptimizerCargoDetailsBeforeOptimizationTableProps
> = (props) => {
  const { selectedAppLanguage } = useAppContext();

  const columns = useMemo(() => {
    return orderOptimizerCargoDetailsBeforeOptimizationTableHelper.getColumns();
  }, [selectedAppLanguage]);

  const createOptimizedStatsTableRow = (
    detailsOptimizedStatsItem: OrderOptimizerCargoDetailsItemOptimizedStats
  ): OrderOptimizerCargoDetailsBeforeOptimizationTableRow => {
    const distanceLabel = orderOptimizerCargoDetailsHelper.getDistanceLabel(
      detailsOptimizedStatsItem.distance
    );
    const distanceTitle = orderOptimizerCargoDetailsHelper.getDistanceLabel(
      detailsOptimizedStatsItem.distance
    );

    const totalTimeLabel = orderOptimizerCargoDetailsHelper.getTimeDeltaLabel(
      detailsOptimizedStatsItem.totalTime
    );
    const totalTimeTitle = orderOptimizerCargoDetailsHelper.getTimeDeltaLabel(
      detailsOptimizedStatsItem.totalTime
    );

    const haltingTimeLabel = orderOptimizerCargoDetailsHelper.getTimeDeltaLabel(
      detailsOptimizedStatsItem.haltingTime
    );
    const haltingTimeTitle = orderOptimizerCargoDetailsHelper.getTimeDeltaLabel(
      detailsOptimizedStatsItem.haltingTime
    );

    const savingLabel = orderOptimizerCargoDetailsHelper.getSavingLabel(
      detailsOptimizedStatsItem.saving
    );
    const savingTitle = orderOptimizerCargoDetailsHelper.getSavingLabel(
      detailsOptimizedStatsItem.saving
    );

    const translations =
      orderOptimizerTranslationsHelper.getCargoDetailsTranslations().table
        .beforeOptimization;

    return {
      id: uuidService.generate(),
      value: {
        optimization: (
          <div title={translations.afterOptimizationTitle}>
            {translations.afterOptimizationLabel}
          </div>
        ),
        billingModel: (
          <div title={props.detailsOrderItems?.[0].billingModel}>
            {props.detailsOrderItems?.[0].billingModel}
          </div>
        ),
        totalTime: <div title={totalTimeTitle}>{totalTimeLabel}</div>,
        haltingTime: <div title={haltingTimeTitle}>{haltingTimeLabel}</div>,
        distance: <div title={distanceTitle}>{distanceLabel}</div>,
        saving: <div title={savingTitle}>{savingLabel}</div>,
      },
    };
  };

  const createUnoptimizedStatsTableRow = (
    detailsUnoptimizedStatsItem: OrderOptimizerCargoDetailsItemUnoptimizedStats
  ): OrderOptimizerCargoDetailsBeforeOptimizationTableRow => {
    const distanceLabel = orderOptimizerCargoDetailsHelper.getDistanceLabel(
      detailsUnoptimizedStatsItem.distance
    );
    const distanceTitle = orderOptimizerCargoDetailsHelper.getDistanceLabel(
      detailsUnoptimizedStatsItem.distance
    );

    const totalTimeLabel = orderOptimizerCargoDetailsHelper.getTimeDeltaLabel(
      detailsUnoptimizedStatsItem.totalTime
    );
    const totalTimeTitle = orderOptimizerCargoDetailsHelper.getTimeDeltaLabel(
      detailsUnoptimizedStatsItem.totalTime
    );

    const haltingTimeLabel = orderOptimizerCargoDetailsHelper.getTimeDeltaLabel(
      detailsUnoptimizedStatsItem.haltingTime
    );
    const haltingTimeTitle = orderOptimizerCargoDetailsHelper.getTimeDeltaLabel(
      detailsUnoptimizedStatsItem.haltingTime
    );

    const translations =
      orderOptimizerTranslationsHelper.getCargoDetailsTranslations().table
        .beforeOptimization;

    return {
      id: uuidService.generate(),
      value: {
        optimization: (
          <div title={translations.sumOfOrdersTitle}>
            {translations.sumOfOrdersLabel}
          </div>
        ),
        billingModel: <div title="---">---</div>,
        totalTime: <div title={totalTimeTitle}>{totalTimeLabel}</div>,
        haltingTime: <div title={haltingTimeTitle}>{haltingTimeLabel}</div>,
        distance: <div title={distanceTitle}>{distanceLabel}</div>,
        saving: <div title="---">---</div>,
      },
    };
  };

  const rows: OrderOptimizerCargoDetailsBeforeOptimizationTableRow[] =
    useMemo(() => {
      if (
        !props.detailsOptimizedStatsItem ||
        !props.detailsUnoptimizedStatsItem
      )
        return [];

      const optimizedRows = createOptimizedStatsTableRow(
        props.detailsOptimizedStatsItem
      );

      const unoptimizedRows = createUnoptimizedStatsTableRow(
        props.detailsUnoptimizedStatsItem
      );

      return [unoptimizedRows, optimizedRows];
    }, [
      props.detailsUnoptimizedStatsItem,
      props.detailsOptimizedStatsItem,
      selectedAppLanguage,
    ]);

  return (
    <TableComponent
      columns={columns}
      rows={rows}
      isLoading={props.isLoading}
      isError={props.isError}
    />
  );
};

export default OrderOptimizerCargoDetailsBeforeOptimizationTableComponent;
